import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import Question from '../../components/Question';

// Import rune images
import fehuImage from '../../../img/runes/image-000.webp';
import uruzImage from '../../../img/runes/image-001.webp';
import thurisazImage from '../../../img/runes/image-002.webp';

const RunesIntroductionLesson = ({ 
  level = 'beginner', 
  onNextLesson, 
  markLessonComplete,
  currentQuestion = 0,
  onQuestionChange,
  currentLesson,
  completedQuestions = [],
  setCompletedQuestions
}) => {
  const sections = [
    {
      id: 'origins',
      title: 'I. Historical Origins of the Runes',
      content: `
        The runes emerged in the Germanic world around the 2nd century CE, though their exact origins remain debated among scholars. 
        The earliest runic inscriptions appear in Denmark and northern Germany, with the famous Meldorf fibula (50 CE) being one of the earliest examples.
        
        The Elder Futhark, the oldest complete runic alphabet, contains 24 characters and was used by Germanic tribes from the 2nd to the 8th centuries CE. 
        The name "Futhark" comes from the first six runes: Fehu, Uruz, Thurisaz, Ansuz, Raidho, and Kaunan.
        
        Archaeological evidence shows runes were used for practical purposes like marking ownership, memorial inscriptions, and magical purposes. 
        The runes were primarily carved into wood, stone, bone, and metal objects.
      `,
      imageUrl: fehuImage,
      questions: {
        beginner: {
          question: "When did the runes first emerge in the Germanic world?",
          options: ["1st century BCE", "2nd century CE", "5th century CE", "8th century CE"],
          correctAnswer: "2nd century CE",
        },
        intermediate: {
          question: "What does the term 'Futhark' refer to?",
          options: [
            "The first six runes of the alphabet",
            "A Germanic god",
            "A type of runic inscription",
            "A historical period"
          ],
          correctAnswer: "The first six runes of the alphabet",
        },
      },
    },
    {
      id: 'development',
      title: 'II. Development and Spread',
      content: `
        The runes spread across Europe with Germanic migrations and trade routes. 
        By the 4th century, runic inscriptions appear in Norway, Sweden, and throughout the Germanic territories.
        
        The Elder Futhark remained largely unchanged for several centuries, suggesting a stable cultural tradition. 
        Runes were used by various Germanic peoples including the Goths, Angles, Saxons, and Norse.
        
        The runic tradition continued through the Viking Age (793-1066 CE), though by this time the alphabet had evolved into the Younger Futhark with only 16 characters in Scandinavia.
        The Anglo-Saxon Futhorc, used in England, expanded to include up to 33 characters to accommodate Old English sounds.
      `,
      imageUrl: uruzImage,
      questions: {
        beginner: {
          question: "How many characters were in the Younger Futhark?",
          options: ["16", "24", "33", "26"],
          correctAnswer: "16",
        },
        intermediate: {
          question: "Which Germanic peoples used the runes?",
          options: [
            "Only the Vikings",
            "Goths, Angles, Saxons, and Norse",
            "Only the Anglo-Saxons",
            "Only the Scandinavians"
          ],
          correctAnswer: "Goths, Angles, Saxons, and Norse",
        },
      },
    },
    {
      id: 'purpose',
      title: 'III. Purpose and Usage',
      content: `
        Runes served multiple purposes in Germanic society. They were primarily used for practical communication, marking ownership of objects, and creating memorial inscriptions.
        
        Many runic inscriptions are memorial stones (bautasteinar) erected to honor the dead. These often include the name of the deceased and sometimes brief descriptions of their deeds or lineage.
        
        Runes were also used for magical purposes, as evidenced by inscriptions on amulets, weapons, and other objects. 
        The word "rune" itself likely derives from Proto-Germanic *rūnō, meaning "secret" or "whisper," suggesting their connection to hidden knowledge and magical practices.
        
        Archaeological finds include runic inscriptions on everyday objects like combs, weapons, and tools, showing their integration into daily life.
      `,
      imageUrl: thurisazImage,
      questions: {
        beginner: {
          question: "What were bautasteinar?",
          options: ["Runic weapons", "Memorial stones", "Magic amulets", "Trade goods"],
          correctAnswer: "Memorial stones",
        },
        intermediate: {
          question: "What does the word 'rune' likely mean in Proto-Germanic?",
          options: [
            "Writing",
            "Secret or whisper",
            "Magic",
            "Stone"
          ],
          correctAnswer: "Secret or whisper",
        },
      },
    },
    {
      id: 'cultural',
      title: 'IV. Cultural Significance',
      content: `
        The runes held deep cultural significance beyond their practical use as a writing system. 
        They were connected to Germanic cosmology and mythology, with each rune having both a phonetic value and symbolic meaning.
        
        The runes were associated with the god Odin, who according to Norse mythology discovered them after hanging himself from Yggdrasil (the World Tree) for nine nights.
        This connection to Odin, the god of wisdom, poetry, and magic, reinforced the runes' association with hidden knowledge and divine revelation.
        
        The runic tradition reflects the Germanic worldview, emphasizing concepts like fate (wyrd), honor, and the interconnectedness of the natural and supernatural worlds.
        Each rune embodies aspects of this worldview, from natural forces to human experiences and spiritual concepts.
      `,
      imageUrl: fehuImage,
      questions: {
        beginner: {
          question: "Which Norse god is associated with the discovery of runes?",
          options: ["Thor", "Odin", "Freyr", "Tyr"],
          correctAnswer: "Odin",
        },
        intermediate: {
          question: "What concept in Germanic worldview is closely tied to the runes?",
          options: [
            "Fate (wyrd)",
            "Justice",
            "Beauty",
            "Strength"
          ],
          correctAnswer: "Fate (wyrd)",
        },
      },
    },
    {
      id: 'legacy',
      title: 'V. Modern Legacy and Study',
      content: `
        The runes have experienced several revivals in modern times, particularly in the 19th and 20th centuries.
        Academic study of runes (runology) began in earnest in the 19th century, with scholars working to decipher inscriptions and understand their historical context.
        
        The runes continue to be studied for their linguistic, historical, and cultural significance.
        Modern practitioners of Germanic paganism and esoteric traditions have revived interest in the runes for divination and spiritual purposes.
        
        Archaeological discoveries continue to add to our understanding of runic usage, with new inscriptions being found regularly.
        The runes remain a powerful symbol of Germanic cultural heritage and continue to inspire interest in ancient Northern European traditions.
      `,
      imageUrl: uruzImage,
      questions: {
        beginner: {
          question: "What is the academic study of runes called?",
          options: ["Runicology", "Runology", "Germanic studies", "Norse studies"],
          correctAnswer: "Runology",
        },
        intermediate: {
          question: "When did academic study of runes begin in earnest?",
          options: [
            "16th century",
            "19th century",
            "20th century",
            "21st century"
          ],
          correctAnswer: "19th century",
        },
      },
    },
  ];

  const [selectedOption, setSelectedOption] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');
  const [allQuestionsAnsweredCorrectly, setAllQuestionsAnsweredCorrectly] = useState(false);

  useEffect(() => {
    if (allQuestionsAnsweredCorrectly) {
      markLessonComplete();
      onNextLesson();
    }
  }, [allQuestionsAnsweredCorrectly, markLessonComplete, onNextLesson]);

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      // Mark this question as completed
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      if (currentQuestion + 1 < sections.length) {
        onQuestionChange(currentQuestion + 1);
      } else {
        setAllQuestionsAnsweredCorrectly(true);
      }
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const getImageStyle = () => {
    const baseStyle = {
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease-in-out'
    };

    if (imageSize === 'smallimage') {
      return {
        ...baseStyle,
        maxWidth: '150px',
        width: '150px'
      };
    } else {
      return {
        ...baseStyle,
        maxWidth: '300px',
        width: '300px'
      };
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  return (
    <div>
      <Helmet>
        <title>Introduction to Runes - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && sections[currentQuestion]?.imageUrl && (
            <div className="has-text-centered mb-4">
              <img
                src={sections[currentQuestion].imageUrl}
                alt={`Runic History - ${sections[currentQuestion].title}`}
                style={getImageStyle()}
              />
              <div className="icon-buttons mt-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="icon-button has-text-warning mr-3"
                  onClick={toggleImageVisibility}
                  style={{ cursor: 'pointer' }}
                />
                <FontAwesomeIcon
                  icon={faExpandArrowsAlt}
                  className="icon-button has-text-warning"
                  onClick={toggleImageSize}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
          )}

          <section className="section has-text-centered" id={sections[currentQuestion]?.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentQuestion]?.title}</h2>
                  <p className="has-text-white">{sections[currentQuestion]?.content}</p>
                </div>
              </div>
            </div>
          </section>

          {sections[currentQuestion]?.questions && (
            <section className="section has-text-centered">
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <Question
                      question={sections[currentQuestion].questions[level].question}
                      options={sections[currentQuestion].questions[level].options}
                      correctAnswer={sections[currentQuestion].questions[level].correctAnswer}
                      onAnswered={handleAnswer}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RunesIntroductionLesson; 